import { useState } from 'react';
import { Dropdown, Menu, Badge } from '@components';
import {
  UserIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { get } from 'lodash';
import { useDeal } from './context';

const Pulse = () => {
  return (
    <span className="z-[10] flex h-3 w-3 absolute top-2 right-2">
      <span className="relative inline-flex rounded-full h-3 w-3 bg-sky-400"></span>
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
    </span>
  );
};

function ButtonIcon({ selected, label, icon, children, menuWidth = 'w-[150px]' }) {
  const Icon = icon;

  return (
    <Dropdown
      button={
        <div
          className={`relative flex flex-col items-center justify-center ${selected ? 'bg-slate-100' : 'hover:bg-slate-50'} p-2 rounded-lg`}
        >
          {selected && <Pulse />}
          <Icon className="w-6 h-6 text-slate-600" />
          <div className="text-xs font-hmedium">{label}</div>
        </div>
      }
    >
      <Menu className={`${menuWidth} p-3`}>
        <div className="flex flex-col">{children}</div>
      </Menu>
    </Dropdown>
  );
}

function FilterButton({ selected, label, onClick }) {
  return (
    <button
      className={`${selected ? 'bg-green-100 text-green-700' : 'hover:bg-slate-100'} rounded px-2 py-1 font-hmedium text-sm inline-flex justify-between`}
      onClick={onClick}
    >
      <span>{label}</span>
      {selected && <CheckIcon className="w-4 h-4" />}
    </button>
  );
}

function FilterCheckbox({ checked, onClick }) {
  return (
    <button className="w-full text-center" onClick={onClick}>
      <input type="checkbox" className="cursor-pointer" checked={checked} />
    </button>
  );
}

function Clearer({ onClick }) {
  return (
    <button className="mt-2" onClick={onClick}>
      <Badge icon={XMarkIcon} label="Supprimer le filtre" />
    </button>
  );
}
export function Filters() {
  const {
    taskItemById,
    availableStates,
    dealObjects,
    filters: {
      selectedEditors,
      setSelectedEditors,
      selectedViewers,
      setSelectedViewers,
      selectedStatuses,
      setSelectedStatuses,
      selectedDeadline,
      setSelectedDeadline
    } = {}
  } = useDeal();

  const members = get(dealObjects, 'DealMember', []);

  return (
    <div className="hidden lg:flex space-x-2 mr-6 items-center">
      <ButtonIcon
        label="Rôle"
        icon={UserIcon}
        menuWidth="w-[350px]"
        selected={
          (Array.isArray(selectedEditors) && selectedEditors.length >= 1) ||
          (Array.isArray(selectedViewers) && selectedViewers.length >= 1)
        }
      >
        <div className="grid grid-cols-[3fr_1fr_1fr] gap-4 border-b border-t py-1 border-slate-200 bg-slate-50 text-xs font-hmedium [&>div]:px-2">
          <div>Intervenant</div>
          <div className="text-center">Affectation</div>
          <div className="text-center">Partage</div>
        </div>
        <div className="py-1">
          {members.map(member => (
            <div className="grid grid-cols-[3fr_1fr_1fr] gap-4 items-center py-1">
              <div className="truncate text-xs font-hmedium px-2">{member.label}</div>
              <FilterCheckbox
                key={`${member.user_account_id}-editor`}
                checked={selectedEditors.includes(member.user_account_id)}
                onClick={() =>
                  setSelectedEditors(prev => {
                    if (prev.includes(member.user_account_id)) {
                      return prev.filter(p => p !== member.user_account_id);
                    } else {
                      return [...prev, member.user_account_id];
                    }
                  })
                }
              />
              <FilterCheckbox
                key={`${member.user_account_id}-viewer`}
                checked={selectedViewers.includes(member.user_account_id)}
                onClick={() =>
                  setSelectedViewers(prev => {
                    if (prev.includes(member.user_account_id)) {
                      return prev.filter(p => p !== member.user_account_id);
                    } else {
                      return [...prev, member.user_account_id];
                    }
                  })
                }
              />
            </div>
          ))}
        </div>
        {(selectedEditors.length > 0 || selectedViewers.length > 0) && (
          <Clearer
            onClick={() => {
              setSelectedEditors([]);
              setSelectedViewers([]);
            }}
          />
        )}
      </ButtonIcon>

      <ButtonIcon
        label="Statut"
        selected={Array.isArray(selectedStatuses) && selectedStatuses.length >= 1}
        icon={CheckCircleIcon}
      >
        <div className="flex flex-col space-y-1">
          {availableStates.map(state => (
            <FilterButton
              key={state.id}
              label={state.label}
              selected={selectedStatuses.includes(state.id)}
              onClick={() =>
                setSelectedStatuses(prev => {
                  if (prev.includes(state.id)) {
                    return prev.filter(p => p !== state.id);
                  } else {
                    return [...prev, state.id];
                  }
                })
              }
            />
          ))}
        </div>
        {selectedStatuses.length > 0 && <Clearer onClick={() => setSelectedStatuses([])} />}
      </ButtonIcon>

      <ButtonIcon
        label="Échéance"
        selected={!!selectedDeadline && selectedDeadline !== ''}
        icon={CalendarDaysIcon}
      >
        <div className="flex flex-col space-y-1">
          <FilterButton
            selected={selectedDeadline === 'UPCOMING'}
            onClick={() =>
              selectedDeadline === 'UPCOMING'
                ? setSelectedDeadline('')
                : setSelectedDeadline('UPCOMING')
            }
            label="À venir"
          />
          <FilterButton
            selected={selectedDeadline === 'CURRENT'}
            onClick={() =>
              selectedDeadline === 'CURRENT'
                ? setSelectedDeadline('')
                : setSelectedDeadline('CURRENT')
            }
            label="Bientôt"
          />
          <FilterButton
            selected={selectedDeadline === 'LATE'}
            onClick={() =>
              selectedDeadline === 'LATE' ? setSelectedDeadline('') : setSelectedDeadline('LATE')
            }
            label="En retard"
          />
        </div>
        {!!selectedDeadline && selectedDeadline !== '' && (
          <Clearer onClick={() => setSelectedDeadline('')} />
        )}
      </ButtonIcon>
    </div>
  );
}
